import hiringManager from "@/assets/boss.png";
import logo from '@/assets/hiredcoach-logo.svg';
import UserAvatar from '@/components/avatar/UserAvatar';
import { useAuth } from '@/contexts/AuthContext';
import { FeedbackResponse, InterviewResponse, JobResponse, QuestionsPayload, useApiClient } from '@/lib/api';
import { CheckCircle, Clock, Lightbulb, MessageCircle, MoveLeft, MoveRight, TrendingUp, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';


interface InterviewFeedbackProps {
    interview: InterviewResponse;
    demoMode?: boolean;
    demoFeedback?: FeedbackResponse[];
    demoJob?: JobResponse;
}

const InterviewFeedback: React.FC<InterviewFeedbackProps> = ({ interview, demoMode, demoFeedback, demoJob }) => {
    const { api } = useApiClient();
    const { user } = useAuth();
    const [feedback, setFeedback] = useState<FeedbackResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedQuestion, setSelectedQuestion] = useState<QuestionsPayload | null>(null);
    const [jobData, setJobData] = useState<JobResponse | null>(null);
    const [overallScore, setOverallScore] = useState<number>(0);
    const [selectedFeedback, setSelectedFeedback] = useState<FeedbackResponse | null>(null);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [activeTab, setActiveTab] = useState<string>('overview');

    const changeQuestionIndex = (offset: number) => {
        const newIndex = questionIndex + offset;
        if (newIndex >= 0 && newIndex < interview.question_order.length) {
            setQuestionIndex(newIndex);
        }
    };

    useEffect(() => {
        setOverallScore(interview.overall_score);
    }, [interview]);

    useEffect(() => {
        if (feedback.length > 0 && interview.question_order.length > 0) {
            const questionId = interview.question_order[questionIndex];
            const question = getQuestion(questionId);
            const feedbackData = getFeedbackByQuestion(questionId);
            setSelectedQuestion(question);
            setSelectedFeedback(feedbackData);
        }
    }, [questionIndex, feedback, interview.question_order, interview.questions]);

    useEffect(() => {
        // Demo mode: use provided demo data and skip API
        if (demoMode) {
            if (demoFeedback) setFeedback(demoFeedback);
            if (demoJob) setJobData(demoJob);
            setLoading(false);
            return;
        }

        const fetchFeedback = async () => {
            try {
                const feedbackData = await api.getFeedback(interview.id);
                setFeedback(feedbackData);
            } catch (error) {
                console.error('Failed to fetch feedback:', error);
            } finally {
                if (interview.job_id) {
                    fetchJob();
                } else {
                    setLoading(false);
                }
            }
        };

        const fetchJob = async () => {
            try {
                const jobResponse = await api.getJobById(interview.job_id);
                setJobData(jobResponse)
            } catch (error) {
                console.log("Failed to fetch job:", error)
            } finally {
                setLoading(false);
            }
        };

        fetchFeedback();

    }, [interview.id, api, demoMode, demoFeedback, demoJob, interview.job_id]);

    const getScoreLabel = (score: number) => {
        if (score > 3.9) return "S"
        else if (score > 3.4) return "A"
        else if (score > 2.9) return "B"
        else if (score > 1.9) return "C"
        else if (score > 1.4) return "D"
        else return "F"
    };

    const getGradeBackgroundColor = (grade: number) => {
        const label = getScoreLabel(grade);
        const gradeColors = {
            S: 'from-amber-400 via-yellow-400 to-amber-500 text-amber-900 shadow-lg shadow-amber-300/50 border border-amber-300',
            A: 'bg-green-300 text-green-900',
            B: 'bg-green-200 text-green-700',
            C: 'bg-yellow-200 text-yellow-700',
            D: 'bg-red-200 text-red-700',
            F: 'bg-red-500 text-red-900',
        };
        return gradeColors[label];
    };

    const getGradeTextColor = (grade: number) => {
        const label = getScoreLabel(grade);
        const gradeTextColors = {
            S: 'text-amber-900 font-bold drop-shadow-sm',
            A: 'text-green-900',
            B: 'text-green-700',
            C: 'text-yellow-700',
            D: 'text-red-700',
            F: 'text-red-900',
        };
        return gradeTextColors[label];
    };

    const getQuestion = (id: string) => {
        if (!interview || !interview.questions) return null;
        return interview.questions.find((question) => question.Question.id === id);
    };

    const getFeedbackByQuestion = (questionId: string) => {
        if (!interview || !feedback) return null;
        return feedback.find((feedback) => feedback.question === questionId);
    };

    if (loading || !interview || !feedback) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-0 sm:space-y-8 -m-4 sm:m-0 p-0 sm:p-6 max-w-7xl sm:mx-auto">
            {/* Header */}
            <div className="bg-white shadow-none sm:shadow-sm border-0 sm:border rounded-none sm:rounded-lg p-4 sm:p-6 mx-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-4 sm:space-y-0">
                    <div className="flex flex-col sm:flex-row items-center sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 text-center sm:text-left">
                        {/* Hide logo on mobile, show on desktop */}
                        <div className="hidden sm:flex w-24 h-24 rounded-full items-center justify-center">
                            <img src={logo} alt="Logo" className="w-24 h-24" />
                        </div>
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                                {jobData ? jobData.title : 'General Interview'}
                            </h1>
                            <p className="text-gray-900">
                                {jobData ? jobData.company : 'Role-agnostic practice'}
                            </p>
                            <p className="text-sm sm:text-base text-gray-600">Interview completed on {new Date(interview.created_at).toLocaleDateString()}</p>
                        </div>
                    </div>
                    {/* Overall Score */}
                    <div className="text-center sm:ml-8">
                        <div className={`text-6xl sm:text-8xl font-bold ${getGradeTextColor(overallScore)}`}>
                            {getScoreLabel(overallScore)}
                        </div>
                        {/* Demo badge removed intentionally; demo behavior preserved silently */}
                    </div>
                </div>
            </div>

            {/* Tabbed Interface */}
            <div className="bg-white shadow-none sm:shadow-sm border-0 sm:border rounded-none sm:rounded-lg overflow-hidden mx-0">
                {/* Tab Navigation */}
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'overview'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('suggestions')}
                            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'suggestions'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Suggestions
                        </button>
                        <button
                            onClick={() => setActiveTab('hiring')}
                            className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${activeTab === 'hiring'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            Hiring Manager
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="p-4 sm:p-6">
                    {activeTab === 'overview' && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Advice</h3>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-gray-700 leading-relaxed">{interview.overall_description}</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
                                {/* The Good */}
                                <div className="bg-green-50 rounded-lg p-4 sm:p-6 border border-green-200">
                                    <div className="flex items-center space-x-2 mb-4">
                                        <CheckCircle className="w-5 h-5 text-green-600" />
                                        <h3 className="text-lg font-semibold text-gray-900">The Good</h3>
                                    </div>
                                    <ul className="space-y-2">
                                        {interview.pros.map((pro, index) => (
                                            <li key={index} className="flex items-start space-x-2">
                                                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                                                <span className="text-gray-700 text-sm">{pro}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Areas for Improvement */}
                                <div className="bg-red-50 rounded-lg p-4 sm:p-6 border border-red-200">
                                    <div className="flex items-center space-x-2 mb-4">
                                        <XCircle className="w-5 h-5 text-red-600" />
                                        <h3 className="text-lg font-semibold text-gray-900">Areas for Improvement</h3>
                                    </div>
                                    <ul className="space-y-2">
                                        {interview.cons.map((con, index) => (
                                            <li key={index} className="flex items-start space-x-2">
                                                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                                                <span className="text-gray-700 text-sm">{con}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'suggestions' && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Suggestions for Improvement</h3>
                            {interview.suggestions.length > 0 ? (
                                <ul className="space-y-3">
                                    {interview.suggestions.map((suggestion, index) => (
                                        <li key={index} className="flex items-start space-x-3 p-3 sm:p-4 rounded-lg">
                                            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm sm:text-base text-gray-700">{suggestion}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-500 italic">No specific suggestions available.</p>
                            )}
                        </div>
                    )}

                    {activeTab === 'hiring' && (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Hiring Manager Considerations</h3>
                            {interview.hiring_considerations.length > 0 ? (
                                <div className="rounded-lg p-4 sm:p-6">
                                    <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                                        <div className="flex w-32 sm:w-36 mx-auto sm:mx-0">
                                            <img
                                                src={hiringManager}
                                                alt="Hiring Manager"
                                                className="w-32 sm:w-36 object-contain"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <ul className="space-y-3">
                                                {interview.hiring_considerations.map((consideration, index) => (
                                                    <li key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg">
                                                        <Clock className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                                                        <span className="text-gray-700">{consideration}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-gray-500 italic">No hiring manager considerations available.</p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Question-by-Question Breakdown - Enhanced Design */}
            <div className="bg-white shadow-none sm:shadow-lg border-0 sm:border border-gray-200 rounded-none sm:rounded-xl overflow-hidden mx-0">
                {/* Card Header with Navigation */}
                <div className={`bg-gradient-to-r ${selectedFeedback ? getGradeBackgroundColor(selectedFeedback.grade) : 'bg-gray-100'} border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4`}>
                    {/* Mobile Layout */}
                    <div className="flex sm:hidden items-center justify-between">
                        <h2 className="text-lg font-bold text-gray-900">Questions overview</h2>
                        <span className="text-sm font-medium text-gray-700">
                            {questionIndex + 1}/{interview.question_order.length}
                        </span>
                    </div>
                    
                    {/* Mobile Grade and Navigation */}
                    <div className="flex sm:hidden items-center justify-between mt-3">
                        <button
                            onClick={() => changeQuestionIndex(-1)}
                            disabled={questionIndex === 0}
                            className={`p-2 rounded-full transition-all duration-200 ${questionIndex === 0
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-blue-600 shadow-md border border-gray-200'
                                }`}
                            title="Previous Question"
                        >
                            <MoveLeft className="w-4 h-4" />
                        </button>
                        
                        <span className="text-lg font-bold px-4 py-2">
                            Grade: {selectedFeedback ? getScoreLabel(selectedFeedback.grade) : 'Skipped'}
                        </span>
                        
                        <button
                            onClick={() => changeQuestionIndex(1)}
                            disabled={questionIndex === interview.question_order.length - 1}
                            className={`p-2 rounded-full transition-all duration-200 ${questionIndex === interview.question_order.length - 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-blue-600 shadow-md border border-gray-200'
                                }`}
                            title="Next Question"
                        >
                            <MoveRight className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:flex sm:items-center sm:justify-between">
                        <div className="flex sm:items-center sm:space-x-4">
                            <h2 className="text-xl font-bold text-gray-900">Questions overview</h2>
                            <div className="flex sm:items-center sm:space-x-2">
                                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                                    Question {questionIndex + 1} of {interview.question_order.length}
                                </span>
                                <span className="text-sm font-semibold px-3 py-1">
                                    Grade: {selectedFeedback ? getScoreLabel(selectedFeedback.grade) : 'Skipped'}
                                </span>
                            </div>
                        </div>

                        {/* Desktop Navigation Controls */}
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => changeQuestionIndex(-1)}
                                disabled={questionIndex === 0}
                                className={`p-3 rounded-full transition-all duration-200 ${questionIndex === 0
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-blue-600 shadow-md border border-gray-200'
                                    }`}
                                title="Previous Question"
                            >
                                <MoveLeft className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => changeQuestionIndex(1)}
                                disabled={questionIndex === interview.question_order.length - 1}
                                className={`p-3 rounded-full transition-all duration-200 ${questionIndex === interview.question_order.length - 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 hover:text-blue-600 shadow-md border border-gray-200'
                                    }`}
                                title="Next Question"
                            >
                                <MoveRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Card Content */}
                <div className="p-4 sm:p-6">
                    {selectedQuestion != null ? (
                        <div className="space-y-4 sm:space-y-6">
                            {/* Question Section */}
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-3 sm:p-4 border-l-4 border-blue-500">
                                <h4 className="font-semibold text-blue-900 mb-2 sm:mb-3 flex items-center text-sm sm:text-base">
                                    <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                    Question {questionIndex + 1}
                                </h4>
                                <p className="text-gray-800 leading-relaxed font-bold text-sm sm:text-base">{selectedQuestion.Question.question}</p>
                            </div>

                            {/* Answer Section */}
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 sm:p-4 border-l-4 border-gray-400">
                                <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center text-sm sm:text-base">
                                    {(demoMode && selectedFeedback?.answer) || selectedQuestion.Answer ? (
                                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                    ) : (
                                        <XCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                    )}
                                    Your Response
                                </h4>
                                {((demoMode && selectedFeedback?.answer) || selectedQuestion.Answer) ? (
                                    <div className="flex items-start gap-3">
                                        <UserAvatar user={user} className="h-9 w-9 border-0 mt-0.5" />
                                        <p className="text-gray-700 leading-relaxed italic text-sm sm:text-base">{(demoMode && selectedFeedback?.answer) || selectedQuestion.Answer?.response}</p>
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="text-gray-500 italic text-sm sm:text-base">Question was skipped</p>
                                    </div>
                                )}
                            </div>

                            {selectedQuestion.Question.expected && (
                                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg p-3 sm:p-4 border-l-4 border-amber-400">
                                    <h4 className="font-semibold text-amber-900 mb-2 sm:mb-3 flex items-center text-sm sm:text-base">
                                        <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                        What the Hiring Manager Was Expecting
                                    </h4>
                                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                                        {selectedQuestion.Question.expected}
                                    </p>
                                </div>
                            )}

                            {/* Feedback Section */}
                            {selectedFeedback && (
                                <>
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 sm:p-4 border-l-4 border-green-500">
                                        <h4 className="font-semibold text-green-900 mb-2 sm:mb-3 flex items-center text-sm sm:text-base">
                                            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                            Performance Feedback
                                        </h4>
                                        <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{selectedFeedback.feedback}</p>
                                    </div>

                                    {/* Hiring Manager Thoughts - Enhanced */}
                                    <div className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6">
                                        <div className="flex-shrink-0 w-20 sm:w-24 mx-auto sm:mx-0">
                                            <img
                                                src={hiringManager}
                                                alt="Hiring Manager"
                                                className="w-20 sm:w-36 object-contain"
                                            />
                                        </div>
                                        <div className="flex-1 w-full">
                                            <h4 className="font-semibold text-amber-900 mb-2 flex items-center text-sm sm:text-base">
                                                <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                                Hiring Manager's Perspective
                                            </h4>
                                            <div className="bg-white rounded-lg p-3 shadow-sm border border-amber-200">
                                                <p className="text-gray-700 leading-relaxed italic text-sm sm:text-base">
                                                    "{selectedFeedback.reasoning}"
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                                <MessageCircle className="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">You skipped this one...</h3>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default InterviewFeedback;
